import React, { useState, useEffect, useContext } from "react"
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import useAwaitableComponent from "use-awaitable-component";
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  TextField,
  Typography,
  InputAdornment,
  FormControl,
  FormHelperText,
} from '@material-ui/core';

import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import { setUser } from "../utils/auth"
import APIS from "../utils/APIS"

import LanguageContext from "../contexts/LanguageContext"
import { renewSocket } from "../contexts/SocketContext"

import MyMiniModal from "../components/ui/MyMiniModal"
import rectBlackCheckWhite from "../images/rectBlackCheckWhite.svg"

const Register = () => {
  const navigate = useNavigate();

  const { actLanguage, languageValues } = useContext(LanguageContext)

  const [code, setCode] = useState("")
  const [step, setStep] = useState(0)

  const [showPassword, setShowPassword] = useState(false)

  const [alertMessage, setAlertMessage] = useState()

  const [status, execute, resolve, reject, reset] = useAwaitableComponent();
  const showModal = status === "awaiting";

  return (
    <>
      <Helmet>
        <title>{languageValues?.registerPage?.pageTitle[actLanguage] || ''} | TheLab</title>
      </Helmet>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center'
        }}
      >
        {step === 0 &&
          <Container maxWidth="sm">
            <Formik
              initialValues={{
                code: '',
              }}
              validationSchema={Yup.object().shape({
                code: Yup.string().max(255).required(`${languageValues?.requiredField[actLanguage]}`)
              })}
              onSubmit={(values, { setSubmitting }) => {
                setAlertMessage("");
                APIS.checkGroupCode({ data: values.code }).then(result => {
                  if (result?.isOk === true) {
                    setStep(1)
                    setCode(values.code)
                  } else {
                    setAlertMessage(languageValues?.registerPage?.invalidCode[actLanguage])
                  }
                  setSubmitting(false);
                })

              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ mb: 2, textAlign:"center" }}>
                    <Typography
                      color="text.blackTitle"
                      variant="h2"
                    >
                      {languageValues?.registerPage?.title[actLanguage]}
                    </Typography>
                  </Box>

                  <TextField
                    error={Boolean(touched.code && errors.code)}
                    autoComplete="off"
                    fullWidth
                    helperText={touched.code && errors.code}
                    margin="normal"
                    name="code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.code}
                    variant="outlined"
                    placeholder={languageValues?.registerPage?.codeField[actLanguage]}
                    sx={{
                      "& .MuiOutlinedInput-root input" : {
                        textAlign:"center !important",
                      },
                      "& .MuiFormHelperText-root" : {
                        textAlign:"center !important",
                      }
                    }}
                  />
                  <Box sx={{ py: 2 }}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {languageValues?.continue[actLanguage]}
                    </Button>
                  </Box>
                  {alertMessage && alertMessage.length > 0 && (
                    <Box
                      sx={{
                        pb: 1,
                        pt: 0
                      }}
                    >
                      <Typography
                        align="center"
                        color="red"
                        variant="body1"
                      >
                        {alertMessage}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{
                    marginTop:"15px",
                    marginBottom:"15px",
                    fontSize:"16px",
                    textAlign:"center",
                    color : "text.primary",
                  }}>
                    {languageValues?.loginPage?.alreadyAccount[actLanguage]}
                    <Link component={RouterLink} to="/login" sx={{marginLeft:"3px"}}>
                      {languageValues?.mainLayout?.itsHere[actLanguage]}
                    </Link>
                  </Box>
                </form>
              )}
            </Formik>
          </Container>
        }

        {step === 1 &&
          <Container maxWidth="sm">
            <Formik
              initialValues={{
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                checkRules: false,
              }}
              validationSchema={Yup.object().shape({
                firstName: Yup.string().min(3, `${languageValues?.requiredField[actLanguage]}`).max(255, `${languageValues?.requiredField[actLanguage]}`).required(`${languageValues?.requiredField[actLanguage]}`),
                lastName: Yup.string().min(3, `${languageValues?.requiredField[actLanguage]}`).max(255, `${languageValues?.requiredField[actLanguage]}`).required(`${languageValues?.requiredField[actLanguage]}`),
                email: Yup.string().email(`${languageValues?.requiredField[actLanguage]}`).max(255).required(`${languageValues?.requiredField[actLanguage]}`),
                password: Yup.string().min(8, `${languageValues?.requiredField[actLanguage]}`).max(255, `${languageValues?.requiredField[actLanguage]}`).required(`${languageValues?.requiredField[actLanguage]}`),
                checkRules : Yup.bool().oneOf([true], `${languageValues?.requiredField[actLanguage]}`)
              })}
              onSubmit={async (values, { setSubmitting }) => {
                setAlertMessage("");
                try {
                  const value = await execute();

                  const newUser = {
                    code: code,
                    firstname: values.firstName,
                    lastname: values.lastName,
                    email: values.email?.trim()?.toLowerCase(),
                    password: values.password,
                    password2: values.password,
                    language : actLanguage,
                  }

                  const register = await APIS.register(newUser)
                  if (register?.isOk === true) {
                    const userData = {
                      email:  values?.email?.trim()?.toLowerCase(),
                      password: values.password,
                      language : actLanguage,
                    }
                    APIS.loginUser(userData).then(result => {
                      if (result?.message === "signin" && result?.user) {
                        setUser(result?.user)
                        renewSocket()
                        navigate("/")
                        return
                      } else {
                        setAlertMessage(languageValues?.loginPage?.incorrectMessage[actLanguage])
                      }
                    })
                  } else if (register?.errors?.email) {
                    setAlertMessage(languageValues?.registerPage?.alreadyUseEmail[actLanguage])
                  } else {
                    alert("Error")
                  }

                } catch (err) { console.log(err) } finally {
                  reset();
                }

              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                touched,
                values
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ mb: 2, textAlign:"center" }}>
                    <Typography
                      color="text.blackTitle"
                      variant="h2"
                    >
                      {languageValues?.onBoarding?.title[actLanguage]}
                    </Typography>
                  </Box>

                  <Box sx={{display:"flex", flexDirection:["column", "row"], columnGap:"20px", rowGap:"10px"}}>
                    <Box sx={{flex:1}}>
                      <Box sx={{textTransform:"uppercase", fontSize:["12px", "15px"], color:"text.black"}}>
                        {languageValues?.registerPage?.firstNameField[actLanguage]}
                      </Box>
                      <TextField
                        error={Boolean(touched.firstName && errors.firstName)}
                        fullWidth
                        helperText={touched.firstName && errors.firstName}
                        margin="normal"
                        name="firstName"
                        type="text"
                        value={values.firstName}
                        placeholder="ex. Paul"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{
                          marginTop:"10px",
                        }}
                      />
                    </Box>
                    <Box sx={{flex:1}}>
                      <Box sx={{textTransform:"uppercase", fontSize:["12px", "15px"], color:"text.black"}}>
                        {languageValues?.registerPage?.lastNameField[actLanguage]}
                      </Box>
                      <TextField
                        error={Boolean(touched.lastName && errors.lastName)}
                        fullWidth
                        helperText={touched.lastName && errors.lastName}
                        margin="normal"
                        name="lastName"
                        type="text"
                        value={values.lastName}
                        placeholder="ex. Durand"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{
                          marginTop:"10px",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{textTransform:"uppercase", fontSize:["12px", "15px"], paddingTop:"10px",}}>
                    {languageValues?.loginPage?.emailField[actLanguage]}
                  </Box>
                  <TextField
                    error={Boolean(touched.email && errors.email)}
                    fullWidth
                    helperText={touched.email && errors.email}
                    margin="normal"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="email"
                    value={values.email}
                    variant="outlined"
                    placeholder="ex. paul.durand@email.com"
                  />
                  <Box sx={{textTransform:"uppercase", fontSize:["12px", "15px"], paddingTop:"10px",}}>
                    {languageValues?.loginPage?.passwordField[actLanguage]}
                  </Box>
                  <TextField
                    error={Boolean(touched.password && errors.password)}
                    fullWidth
                    helperText={touched.password && errors.password}
                    margin="normal"
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    variant="outlined"
                    placeholder={languageValues?.loginPage?.passwordField[actLanguage]}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{cursor:"pointer"}} onClick={() => { setShowPassword(!showPassword) }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ paddingTop:"10px",paddingBottom:"5px"}}>
                    <Box sx={{display:"flex", gap:"10px", alignItems:"center"}}>
                      <Box
                        sx={{
                          width:["21px", "26px"],
                          height:["21px", "26px"],
                          cursor:"pointer",
                          backgroundImage:values?.checkRules === false ? "" : `url(${rectBlackCheckWhite})`,
                          backgroundSize:"contain",
                          backgroundRepeat:"no-repeat",
                          backgroundPosition:"center center",
                          border: values?.checkRules === false ? "1px solid black" : "0px",
                        }}
                        onClick={() => {
                          setFieldValue("checkRules", !values.checkRules)
                        }}
                      />
                      <Box sx={{
                        fontSize:"15px",
                        textAlign:"center",
                        color : "text.black",
                      }}>
                        {languageValues?.registerPage?.ruTexte[actLanguage]}
                        <Link component={RouterLink} sx={{marginLeft:"2px"}} onClick={() => {
                          if (actLanguage === "fr") {
                            window.open(window.location.origin+"/terms_FR", "_blank")
                          } else {
                            window.open(window.location.origin+"/terms_EN", "_blank")
                          }
                        }}>
                          {languageValues?.registerPage?.ruTitle[actLanguage]}
                        </Link>
                      </Box>
                    </Box>
                    <FormControl error={Boolean(touched.checkRules && errors.checkRules)}>
                      <FormHelperText>{touched.checkRules && errors.checkRules}</FormHelperText>
                    </FormControl>
                  </Box>
                  <Box sx={{ py: 2 }}>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      {languageValues?.continue[actLanguage]}
                    </Button>
                  </Box>
                  {alertMessage && alertMessage.length > 0 && (
                    <Box
                      sx={{
                        pb: 1,
                        pt: 0
                      }}
                    >
                      <Typography
                        align="center"
                        color="red"
                        variant="body1"
                      >
                        {alertMessage}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{
                    marginTop:"15px",
                    marginBottom:"15px",
                    fontSize:"16px",
                    textAlign:"center",
                    color : "text.primary",
                  }}>
                    {languageValues?.loginPage?.alreadyAccount[actLanguage]}
                    <Link component={RouterLink} to="/login" sx={{marginLeft:"3px"}}>
                      {languageValues?.mainLayout?.itsHere[actLanguage]}
                    </Link>
                  </Box>
                </form>
              )}
            </Formik>
          </Container>
        }

        <MyMiniModal
          isOpen={showModal}
          onClose={reject}
          title={languageValues?.registerPage?.confirmDialog?.title[actLanguage]}
          subTitle={languageValues?.registerPage?.confirmDialog?.subTitle[actLanguage]}
        >
          <Box sx={{
            display:"flex",
            justifyContent:"center",
          }}>
            <Button
              color="primary"
              size="large"
              type="submit"
              variant="contained"
              onClick={resolve}
            >
              {languageValues?.continue[actLanguage]}
            </Button>
          </Box>
        </MyMiniModal>
      </Box>
    </>
  );
};

export default Register;
